// Rapportvakt — the drawn payoff for a Punktlista point.
//
// A point gets exactly one of three things here: the columns chart, a small
// verdict chip, or nothing — decided purely from that point's own
// evaluation, never from what any other point in the delivery is doing (see
// the file-level note in visualRules.js: points are independent, there is no
// competition and no delivery-wide winner). The decision itself is
// visualOutcome() in visualRules.js, not this file — Delivery.jsx runs
// evaluatePoint() once per point and passes both the point and its
// evaluation down here.

import React from 'react';
import { useRvLang } from './Shell';
import { visualOutcome } from './visualRules';

const ROLE_COLOR = {
  actual: 'var(--rv-accent)',
  ref: '#a9967e',
  past: '#a9967e',
  forecast: 'var(--rv-accent)',
};

function roleFill(role) {
  return ROLE_COLOR[role] || '#a9967e';
}

function roleOpacity(role) {
  // Forecast stays in the accent but reads as "not yet real": a lighter fill
  // rather than a second color, so the legend never needs a third swatch the
  // data doesn't already name.
  return role === 'forecast' ? 0.55 : 1;
}

/** Flatten either visual shape (flat `columns`, or `groups` of `values`) into
 * one ordered list of { key, label, value, valueLabel, role, tag }, plus the
 * group label a grouped chart draws under each cluster. */
function flattenColumns(visual) {
  if (Array.isArray(visual.columns)) {
    return visual.columns.map((col, i) => ({
      key: `c${i}`,
      groupLabel: col.label,
      label: col.label,
      value: col.value,
      valueLabel: col.valueLabel,
      role: col.role,
      tag: col.tag,
    }));
  }
  if (Array.isArray(visual.groups)) {
    const out = [];
    visual.groups.forEach((group, gi) => {
      (group.values || []).forEach((v, vi) => {
        out.push({
          key: `g${gi}-${vi}`,
          groupLabel: group.label,
          label: null,
          value: v.value,
          valueLabel: v.valueLabel,
          role: v.role,
          tag: v.tag,
          groupIndex: gi,
        });
      });
    });
    return out;
  }
  return [];
}

const W = 320;
const H = 200;
const PAD_BOTTOM = 40; // room for the column label plus an optional tag
const PAD_SIDE = 10;

function ColumnsChart({ visual }) {
  const { t } = useRvLang();
  const items = flattenColumns(visual);
  const max = typeof visual.max === 'number' && visual.max > 0 ? visual.max : null;
  if (items.length === 0 || !max) return null;

  const benchmarkValue = typeof visual.benchmark?.value === 'number' ? visual.benchmark.value : null;

  // The benchmark's own caption lives in a fixed row above the plot rather
  // than inline at the line's own height: a value close to the benchmark
  // (which is exactly the case departureScore favors) would otherwise sit at
  // almost the same y as a column's value label, and the caption is long
  // enough to run straight through it. Stacking title then caption in their
  // own reserved rows means neither can ever collide with a drawn value,
  // regardless of what the data happens to be.
  const titleRow = visual.title ? 14 : 0;
  const benchmarkRow = benchmarkValue !== null ? 14 : 0;
  const plotTop = 22 + titleRow + benchmarkRow;
  const plotBottom = H - PAD_BOTTOM;
  const plotHeight = plotBottom - plotTop;
  const plotLeft = PAD_SIDE;
  const plotRight = W - PAD_SIDE;
  const plotWidth = plotRight - plotLeft;

  const grouped = Array.isArray(visual.groups);
  const gap = grouped ? 22 : 14;
  const barWidth = (plotWidth - gap * (items.length - 1)) / items.length;

  const yFor = (value) => {
    const v = typeof value === 'number' ? value : 0;
    const ratio = Math.max(0, Math.min(1, v / max));
    return plotBottom - ratio * plotHeight;
  };

  const benchmarkY = benchmarkValue !== null ? yFor(benchmarkValue) : null;

  const legendEntries = grouped ? visual.series || [] : visual.legend || [];

  // Group labels (for the grouped shape) sit centered under each cluster of
  // bars, once per group rather than once per bar.
  const groupLabelPositions = [];
  if (grouped) {
    let seen = new Set();
    items.forEach((item) => {
      if (seen.has(item.groupIndex)) return;
      seen.add(item.groupIndex);
      const groupItems = items.filter((it) => it.groupIndex === item.groupIndex);
      const firstIdx = items.indexOf(groupItems[0]);
      const lastIdx = items.indexOf(groupItems[groupItems.length - 1]);
      const x1 = plotLeft + firstIdx * (barWidth + gap);
      const x2 = plotLeft + lastIdx * (barWidth + gap) + barWidth;
      groupLabelPositions.push({ x: (x1 + x2) / 2, label: item.groupLabel });
    });
  }

  const ariaLabel = [
    visual.title,
    benchmarkValue !== null ? `${t.benchmarkLabel}${visual.benchmark.label}` : null,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <svg
      className="rv-visual__chart"
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      role="img"
      aria-label={ariaLabel}
    >
      {visual.title ? (
        <text x={plotLeft} y={14} className="rv-visual__title">
          {visual.title}
        </text>
      ) : null}

      {/* The benchmark's caption sits in its own reserved row, a small dash
          swatch then the verbatim label, never at the line's own height —
          see the comment on plotTop above for why. */}
      {benchmarkValue !== null ? (
        <g transform={`translate(${plotLeft}, ${14 + titleRow})`}>
          <line x1={0} y1={-4} x2={12} y2={-4} className="rv-visual__benchmark" />
          <text x={17} y={0} className="rv-visual__benchmark-label">
            {visual.benchmark.label}
          </text>
        </g>
      ) : null}

      {/* zero baseline, drawn even when there is no benchmark */}
      <line
        x1={plotLeft}
        y1={plotBottom}
        x2={plotRight}
        y2={plotBottom}
        className="rv-visual__axis"
      />

      {benchmarkY !== null ? (
        <line
          x1={plotLeft}
          y1={benchmarkY}
          x2={plotRight}
          y2={benchmarkY}
          className="rv-visual__benchmark"
        />
      ) : null}

      {items.map((item, i) => {
        const x = plotLeft + i * (barWidth + gap);
        const y = yFor(item.value);
        const barHeight = Math.max(0, plotBottom - y);
        const fill = roleFill(item.role);
        const opacity = roleOpacity(item.role);
        return (
          <g key={item.key}>
            <rect
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              rx={2.5}
              fill={fill}
              opacity={opacity}
            />
            <text
              x={x + barWidth / 2}
              y={Math.max(11, y - 6)}
              textAnchor="middle"
              className="rv-visual__value"
            >
              {item.valueLabel}
            </text>
            {!grouped && item.label ? (
              <text
                x={x + barWidth / 2}
                y={plotBottom + 16}
                textAnchor="middle"
                className="rv-visual__label"
              >
                {item.label}
              </text>
            ) : null}
            {item.tag ? (
              <text
                x={x + barWidth / 2}
                y={plotBottom + (grouped ? 16 : 30)}
                textAnchor="middle"
                className="rv-visual__tag"
              >
                {item.tag}
              </text>
            ) : null}
          </g>
        );
      })}

      {grouped
        ? groupLabelPositions.map((g, i) => (
            <text
              key={i}
              x={g.x}
              y={plotBottom + 16}
              textAnchor="middle"
              className="rv-visual__label"
            >
              {g.label}
            </text>
          ))
        : null}

      {legendEntries.length > 0 ? (
        <g transform={`translate(${plotLeft}, ${H - 6})`}>
          {legendEntries.map((entry, i) => {
            const lx = i * 150;
            return (
              <g key={i} transform={`translate(${lx}, 0)`}>
                <rect
                  x={0}
                  y={-9}
                  width={9}
                  height={9}
                  rx={2}
                  fill={roleFill(entry.role)}
                  opacity={roleOpacity(entry.role)}
                />
                <text x={14} y={-1} className="rv-visual__legend">
                  {entry.label}
                </text>
              </g>
            );
          })}
        </g>
      ) : null}
    </svg>
  );
}

function VerdictGlyph({ tone }) {
  const common = {
    width: 12,
    height: 12,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
  };
  if (tone === 'under') {
    // an arrow pointing down onto a baseline
    return (
      <svg {...common}>
        <path d="M8 3v7M5 7l3 3 3-3" />
        <path d="M3.5 13h9" />
      </svg>
    );
  }
  if (tone === 'up' || tone === 'over') {
    return (
      <svg {...common}>
        <path d="M8 13V6M5 9l3-3 3 3" />
        <path d="M3.5 3h9" />
      </svg>
    );
  }
  // otherwise: a level line
  return (
    <svg {...common}>
      <path d="M3.5 8h9" />
    </svg>
  );
}

function VerdictChip({ verdict }) {
  if (!verdict?.text) return null;
  return (
    <span className="rv-verdict">
      <VerdictGlyph tone={verdict.tone} />
      {verdict.text}
    </span>
  );
}

/**
 * Renders the chart when `evaluation` (this point's own evaluatePoint()
 * result) resolves to 'chart', the verdict chip when it resolves to 'chip',
 * or nothing. The decision itself is visualOutcome() in visualRules.js —
 * this component never calls evaluatePoint or evaluateDelivery itself, and
 * never looks at any other point in the delivery.
 */
export function PointVisual({ point, evaluation }) {
  const outcome = visualOutcome(point, evaluation);
  if (outcome === 'chart') {
    return (
      <div className="rv-visual">
        <ColumnsChart visual={point.visual} />
      </div>
    );
  }
  if (outcome === 'chip') {
    return <VerdictChip verdict={point.verdict} />;
  }
  return null;
}
